import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'

export type ProjectStatisticsParams = {
  start_date?: string
  end_date?: string
}

type ProjectStatisticsConfig = {
  fetchOnMount?: boolean | 'force'
  params: ProjectStatisticsParams
}

export async function get(
  slugOrId: string | number,
  params: URLSearchParams,
): Promise<api.HttpResult<api.ProjectStatistics>> {
  const response = await api.client('GET /projects/:id/stats', {
    urlParams: { id: String(slugOrId) },
    getParams: params,
  })

  return response.next((success) => success.body)
}

function toUrlSearchParams(params: ProjectStatisticsParams): URLSearchParams {
  const search = new URLSearchParams()

  if (params.start_date) {
    search.set('start_date', String(params.start_date))
  }

  if (params.end_date) {
    search.set('end_date', String(params.end_date))
  }

  return search
}

const PROJECT_STATISTICS_ATOM = jotai.atom<hooks.Cache<api.ProjectStatistics>>({})

export function useProjectStatistics(
  slugOrId: string | number,
  { fetchOnMount = false, params }: ProjectStatisticsConfig,
) {
  const { query: queryProjectStatistics, getCurrentResponse } = hooks.useSharedQuery(
    (slugOrId, params) => {
      return get(slugOrId, toUrlSearchParams(params))
    },
    {
      atom: PROJECT_STATISTICS_ATOM,
      id,
    },
  )

  hooks.useFetchOnMount(
    {
      fetchOnMount,
      query,
      getResponse,
    },
    id(slugOrId, params),
  )

  return { response: getResponse(), query }

  function getResponse(): hooks.Response<api.ProjectStatistics> {
    const response = getCurrentResponse([slugOrId, params])

    if (response === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return response
  }

  async function query() {
    return queryProjectStatistics(slugOrId, params)
  }
}

function id(slugOrId: number | string, params: ProjectStatisticsParams): string {
  return JSON.stringify([slugOrId, params.end_date, params.start_date])
}
